import { paygQueryKeys, PendingTopup } from '@/src/api/payg';
import { useAccountContext } from '@ovotech/ui-tools';
import { useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from 'react-query';

const REFRESH_TRIGGER_STATES: Array<PendingTopup['state']> = [
  'PENDING_PAYMENT',
  'PROCESSING',
  'SUCCESSFULLY_APPLIED',
];

type StateCounts = Partial<Record<PendingTopup['state'], number>>;

export const useTopupsRefresher = (topups?: PendingTopup[]) => {
  const queryClient = useQueryClient();
  const { accountId } = useAccountContext();

  const currentStateCounts = useMemo<StateCounts>(() => {
    if (!topups) {
      return {};
    }
    // Group topups by state and count them
    return topups.reduce((acc, topup) => {
      if (REFRESH_TRIGGER_STATES.includes(topup.state)) {
        acc[topup.state] = (acc[topup.state] || 0) + 1;
      }
      return acc;
    }, {} as StateCounts);
  }, [topups]);

  const previousStateCounts = useRef<StateCounts>(currentStateCounts);

  useEffect(() => {
    const prevCounts = previousStateCounts.current;

    // Check if any of the tracked state counts have changed
    const hasChanged = REFRESH_TRIGGER_STATES.some(
      state => (prevCounts[state] || 0) !== (currentStateCounts[state] || 0),
    );

    if (hasChanged && accountId) {
      // Refetch topup history and balances when the number of topups in tracked states changes
      queryClient.invalidateQueries(paygQueryKeys.topups(accountId));
      queryClient.invalidateQueries(paygQueryKeys.balances(accountId));
    }
    previousStateCounts.current = currentStateCounts;
  }, [currentStateCounts, queryClient, accountId]);
};
